import React, { useCallback, useEffect, useRef, useState } from "react"
import {
    GoogleMap,
    Marker,
    MarkerClusterer,
    InfoWindow,
    useJsApiLoader,
    Autocomplete,
} from "@react-google-maps/api"
import SlidingPane from "react-sliding-pane"
import "react-sliding-pane/dist/react-sliding-pane.css"

import Tools from "./overlayTools/Tools"
import MapInfoCard from "./MapInfoCard"
import config from "../../config"
import { locales } from "../../utils/locale/MomentLocale"
import { parseBool, readableDistance } from "../../utils/functions"
import mapStyles from "./mapStyles"
import "./map.css"

// ─── Constants ──────────────────────────────────────────────

const LIBRARIES = ["places"]

const MAP_CONTAINER_DEFAULT = {
    height: "100vh",
    width: "100vw",
}

const MAP_OPTIONS = {
    styles: mapStyles,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
}

// ─── Helpers ────────────────────────────────────────────────

function getMarkerIcon(marker, filterList) {
    const bt = filterList.find((item) => item.key === parseInt(marker.btid))
    if (marker.is_xml === 1) {
        return {
            url: require("../../assets/fnac.png"),
            origin: new window.google.maps.Point(0, 0),
            anchor: new window.google.maps.Point(1, 1),
            scaledSize: new window.google.maps.Size(25, 25),
        }
    }
    const iconName = bt?.icon ?? "j"
    return {
        url: require(`../../assets/icons/${iconName}.png`),
        origin: new window.google.maps.Point(0, 0),
        anchor: new window.google.maps.Point(1, 1),
        scaledSize: new window.google.maps.Size(25, 25),
    }
}

function isMarkerVisible(marker, filterList, filterActive) {
    if (!filterActive) return true
    const bt = filterList.find((item) => item.key === parseInt(marker.btid))
    return bt?.raised === true
}

function jitterLat(lat) {
    return parseFloat(lat) + Math.random() * (0.00001 - 0.00005)
}

/**
 * Safely extract map bounds without using eval()
 */
function extractBounds(mapRef) {
    const bounds = mapRef.getBounds()
    if (!bounds) return null

    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()

    const lowBounds = `${sw.lat()} ${sw.lng()}`
    const highBounds = `${ne.lat()} ${ne.lng()}`
    return `${lowBounds},${highBounds}`
}

// ─── Component ──────────────────────────────────────────────

export default function Map(props) {
    locales("fr")

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: LIBRARIES,
    })

    // ── State ──
    const [openPanel, setOpenPanel] = useState(false)
    const [showCluster, setShowCluster] = useState(
        parseBool(localStorage.getItem("showCluster")) ?? true
    )
    const [memoPlaceList, setMemoPlaceList] = useState(
        JSON.parse(localStorage.getItem("memoPlace") || "[]")
    )
    const [markers, setMarkers] = useState([])
    const [selected, setSelected] = useState(null)
    const [mapContainerStyle, setMapContainerStyle] = useState(MAP_CONTAINER_DEFAULT)
    const [userPos, setUserPos] = useState(null)
    const [filterList, setFilterList] = useState(props.filterList)
    const [filterActive, setFilterActive] = useState(props.filterStatus)
    const [mapRef, setMapRef] = useState(null)
    const [showPartner, setShowPartner] = useState(
        parseBool(localStorage.getItem("showPartner")) ?? false
    )
    const [autoRef, setAutoRef] = useState(null)

    // ── Effects ──

    useEffect(() => {
        setMarkers(props.eventMarkers ?? [])
    }, [props.eventMarkers])

    // ── Map callbacks ──

    const onMapLoad = useCallback((map) => {
        if (!map) return
        setMapRef(map)
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const pos = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                }
                setUserPos(pos)
                localStorage.setItem("lat", pos.lat.toString())
                localStorage.setItem("long", pos.lng.toString())
            },
            () => null
        )
    }, [])

    const handleMoveTheMap = useCallback(
        (reset = false) => {
            if (!mapRef) return
            const boundaries = extractBounds(mapRef)
            const region = mapRef.getCenter()
            if (region && boundaries) {
                props.move(boundaries, region, reset)
            }
        },
        [mapRef, props]
    )

    // ── Marker selection (enrichit avec distance) ──

    const handleSelectMarker = useCallback(
        (marker) => {
            if (userPos?.lat && userPos?.lng) {
                const distance = readableDistance(userPos.lat, userPos.lng, marker.lat, marker.lng)
                if (distance) marker.distance = distance
            }
            setSelected(marker)
        },
        [userPos]
    )

    // ── Panel handlers ──

    const handleChangeFilter = useCallback(
        (key) => {
            const index = filterList.findIndex((x) => x.key === key)
            if (index === -1) return

            const updated = [...filterList]
            updated[index] = { ...updated[index], raised: !updated[index].raised }
            setFilterList(updated)

            const anyActive = updated.some((item) => item.raised)
            setFilterActive(anyActive)
            props.filterOp(updated)
            props.filterStatusOp(anyActive)
        },
        [filterList, props]
    )

    const handleResetFilter = useCallback(() => {
        const reset = filterList.map((item) => ({ ...item, raised: false }))
        setFilterList(reset)
        setFilterActive(false)
        props.filterOp(reset)
        props.filterStatusOp(false)
        setOpenPanel(false)
    }, [filterList, props])

    const handleChangeClusterMode = useCallback(() => {
        localStorage.setItem("showCluster", String(!showCluster))
        setShowCluster(!showCluster)
    }, [showCluster])

    const handleChangePartnerMode = useCallback(() => {
        props.partnersOp(!showPartner)
        localStorage.setItem("showPartner", String(!showPartner))
        setShowPartner(!showPartner)
    }, [showPartner, props])

    const handleChangePosition = useCallback(() => {
        if (!autoRef) return
        const place = autoRef.getPlace()
        if (!place?.geometry) return
        setUserPos({
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
        })
        setTimeout(() => handleMoveTheMap(true), 300)
    }, [autoRef, handleMoveTheMap])

    const handleChangePositionFromUserList = useCallback(
        (e) => {
            const parts = e.target.value.split(",")
            if (parts.length < 2) return
            setUserPos({
                lat: parseFloat(parts[0]),
                lng: parseFloat(parts[1]),
            })
            setTimeout(() => handleMoveTheMap(true), 500)
        },
        [handleMoveTheMap]
    )

    const handleAddMemoPlace = useCallback(() => {
        if (!autoRef) return
        const place = autoRef.getPlace()
        if (!place?.geometry) return
        const newPlace = {
            name: `${place.address_components?.[0]?.long_name ?? ""}, ${place.name ?? ""}`,
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
        }
        const updated = [...memoPlaceList, newPlace]
        setMemoPlaceList(updated)
        localStorage.setItem("memoPlace", JSON.stringify(updated))
        localStorage.setItem("lat", String(newPlace.lat))
        localStorage.setItem("long", String(newPlace.lng))
    }, [autoRef, memoPlaceList])

    const handleDeleteMemoPlaceItem = useCallback(() => {
        if (!userPos || memoPlaceList.length < 1) return
        const filtered = memoPlaceList.filter(
            (place) => place.lat !== userPos.lat || place.lng !== userPos.lng
        )
        setMemoPlaceList(filtered)
        localStorage.setItem("memoPlace", JSON.stringify(filtered))
    }, [memoPlaceList, userPos])

    // ── Map size transitions ──

    const reduceMapSize = useCallback(() => {
        setMapContainerStyle({
            transition: "height 1000ms ease-in-out",
            height: "40vh",
            width: "100vw",
        })
    }, [])

    const maximizeMapSize = useCallback(() => {
        setMapContainerStyle({
            transition: "height 800ms ease-in-out",
            height: "100vh",
            width: "100vw",
        })
    }, [])

    // ── Icons ──

    const iconIAmHere = isLoaded
        ? {
            url: require("../../assets/iamhere.png"),
            origin: new window.google.maps.Point(0, 0),
            anchor: new window.google.maps.Point(15, 15),
            scaledSize: new window.google.maps.Size(50, 50),
        }
        : null

    // ── Render helpers ──

    const renderMarker = useCallback(
        (marker, index, clusterer = undefined) => {
            if (!isMarkerVisible(marker, filterList, filterActive)) return null

            const icon = getMarkerIcon(marker, filterList)
            return (
                <Marker
                    key={`marker-${marker.ev_id ?? index}`}
                    position={{
                        lat: jitterLat(marker.lat),
                        lng: parseFloat(marker.lng),
                    }}
                    onClick={() => handleSelectMarker(marker)}
                    icon={icon}
                    clusterer={clusterer}
                />
            )
        },
        [filterList, filterActive, handleSelectMarker]
    )

    // ── Guard ──

    if (loadError) {
        return (
            <div className="map-error">
                <p>Erreur de chargement de la carte</p>
            </div>
        )
    }

    if (!isLoaded) {
        return (
            <div className="map-loading">
                <div className="map-loading__spinner" />
                <p>Chargement de la carte…</p>
            </div>
        )
    }

    // ── Main render ──

    return (
        <div className="map-wrapper">
            {/* ═══ Settings Panel ═══ */}
            <SlidingPane
                closeIcon={<span className="panel-close">✕</span>}
                isOpen={openPanel}
                title="Mes paramètres"
                from="right"
                width="min(380px, 90vw)"
                onRequestClose={() => setOpenPanel(false)}
                className="map-panel"
            >
                <div className="map-panel__content">
                    {/* Partner toggle */}
                    <section className="map-panel__section">
                        <p className="map-panel__label">Événements FNAC Spectacles</p>
                        <label className="map-toggle">
                            <input
                                type="checkbox"
                                checked={showPartner}
                                onChange={handleChangePartnerMode}
                            />
                            <span className="map-toggle__slider" />
                            <span className="map-toggle__text">
                {showPartner ? "Activé" : "Désactivé"}
              </span>
                        </label>
                    </section>

                    <hr className="map-panel__divider" />

                    {/* Cluster toggle */}
                    <section className="map-panel__section">
                        <p className="map-panel__label">Grouper les événements</p>
                        <label className="map-toggle">
                            <input
                                type="checkbox"
                                checked={showCluster}
                                onChange={handleChangeClusterMode}
                            />
                            <span className="map-toggle__slider" />
                            <span className="map-toggle__text">
                {showCluster ? "Activé" : "Désactivé"}
              </span>
                        </label>
                    </section>

                    <hr className="map-panel__divider" />

                    {/* Autocomplete place search */}
                    <section className="map-panel__section">
                        <p className="map-panel__label">Changer de localité</p>
                        <div className="map-panel__search-row">
                            <Autocomplete
                                onLoad={(auto) => setAutoRef(auto)}
                                onPlaceChanged={handleChangePosition}
                                className="map-panel__autocomplete"
                            >
                                <input
                                    type="text"
                                    placeholder="Rechercher un lieu…"
                                    className="map-panel__input"
                                />
                            </Autocomplete>
                            <button
                                className="map-panel__btn-icon"
                                onClick={handleAddMemoPlace}
                                title="Sauvegarder ce lieu"
                            >
                                +
                            </button>
                        </div>
                    </section>

                    <hr className="map-panel__divider" />

                    {/* Saved places */}
                    <section className="map-panel__section">
                        <p className="map-panel__label">Lieux sauvegardés</p>
                        <select
                            className="map-panel__select"
                            onChange={handleChangePositionFromUserList}
                            defaultValue="-1"
                        >
                            <option value="-1">Aucun lieu choisi</option>
                            {memoPlaceList.map((place, i) =>
                                place.name?.length > 1 ? (
                                    <option key={i} value={`${place.lat},${place.lng}`}>
                                        {place.name}
                                    </option>
                                ) : null
                            )}
                        </select>
                        <button
                            className="map-panel__btn-text"
                            onClick={handleDeleteMemoPlaceItem}
                        >
                            Supprimer ce lieu
                        </button>
                    </section>

                    <hr className="map-panel__divider" />

                    {/* Filters */}
                    <section className="map-panel__section">
                        <p className="map-panel__label">Filtres</p>
                        <div className="map-panel__filters">
                            {filterList?.map((type) => (
                                <button
                                    key={type.key}
                                    className={`map-panel__filter-btn ${
                                        type.raised ? "map-panel__filter-btn--active" : ""
                                    }`}
                                    onClick={() => handleChangeFilter(type.key)}
                                    title={type.label ?? ""}
                                >
                                    <i className={`fa fa-${type.icon} fa-lg`} />
                                </button>
                            ))}
                        </div>
                        {filterActive && (
                            <button className="map-panel__btn-text" onClick={handleResetFilter}>
                                Effacer les filtres
                            </button>
                        )}
                    </section>
                </div>
            </SlidingPane>

            {/* ═══ Google Map ═══ */}
            <GoogleMap
                options={MAP_OPTIONS}
                onLoad={onMapLoad}
                id="main-map"
                mapContainerStyle={mapContainerStyle}
                zoom={props.zoom}
                center={userPos}
                onDragEnd={() => handleMoveTheMap()}
            >
                <Tools
                    reduceMySize={reduceMapSize}
                    maximizeMySize={maximizeMapSize}
                    openPanel={() => setOpenPanel((prev) => !prev)}
                />

                {/* ── Markers (clustered or flat) ── */}
                {showCluster ? (
                    <MarkerClusterer>
                        {(clusterer) =>
                            markers.map((marker, index) => renderMarker(marker, index, clusterer))
                        }
                    </MarkerClusterer>
                ) : (
                    markers.map((marker, index) => renderMarker(marker, index))
                )}

                {/* ── User position ── */}
                {userPos && (
                    <Marker
                        key="user-position"
                        position={{ lat: parseFloat(userPos.lat), lng: parseFloat(userPos.lng) }}
                        icon={iconIAmHere}
                        zIndex={999}
                    />
                )}

                {/* ── InfoWindow ── */}
                {selected && (
                    <InfoWindow
                        position={{
                            lat: parseFloat(selected.lat),
                            lng: parseFloat(selected.lng),
                        }}
                        onCloseClick={() => setSelected(null)}
                        options={{ maxWidth: 420, pixelOffset: new window.google.maps.Size(0, -5) }}
                    >
                        <MapInfoCard event={selected} onClose={() => setSelected(null)} />
                    </InfoWindow>
                )}
            </GoogleMap>
        </div>
    )
}
